// Mayday Cloud design tokens.
//
// Palette comes from the Mayday brand kit (shared with Studio and Live):
//   Abyss #231F20  text on light, dark UI base
//   Brick #BB4430  the sync dot: uploading, syncing, live. Nothing else.
//   Tide  #7EBDC2  large puff: files, the interactive accent in the app
//   Sand  #F3DFA2  small puff: tags, warnings, the folder color
//   Foam  #EFE6DD  text on dark
//
// Surfaces are warm near-blacks derived from Abyss so the UI reads as one
// family with the logo, instead of the cool slate most dark themes use.
// Pages keep inline style objects; they compose from these tokens and the
// `btn` / `input` / `dialog` mixins below so every surface agrees.

export const brand = {
  abyss: '#231F20',
  brick: '#BB4430',
  tide: '#7EBDC2',
  sand: '#F3DFA2',
  foam: '#EFE6DD',
};

export const colors = {
  // Surfaces, darkest to lightest
  bg: '#151213',
  panel: '#1a1718',
  raised: '#231F20',
  overlay: '#2a2526',
  inputBg: 'rgba(239,230,221,0.045)',
  scrim: 'rgba(21,18,19,0.72)',

  // Lines and states
  border: 'rgba(239,230,221,0.07)',
  borderStrong: 'rgba(239,230,221,0.13)',
  hover: 'rgba(239,230,221,0.045)',
  active: 'rgba(239,230,221,0.09)',

  // Text
  text: '#EFE6DD',
  textSecondary: 'rgba(239,230,221,0.66)',
  textMuted: 'rgba(239,230,221,0.42)',
  textFaint: 'rgba(239,230,221,0.26)',

  // Tide: selection, focus, links, primary actions, "synced" states
  accent: '#7EBDC2',
  accentText: '#9DD0D4',
  accentSoft: 'rgba(126,189,194,0.12)',
  accentBorder: 'rgba(126,189,194,0.4)',
  onAccent: '#151213',

  // Brick: upload buttons and in-progress sync states only
  sync: '#BB4430',
  syncText: '#E48A75',
  syncSoft: 'rgba(187,68,48,0.14)',
  syncBorder: 'rgba(187,68,48,0.4)',
  onSync: '#EFE6DD',

  // Sand: tags, warnings, folders
  sand: '#F3DFA2',
  sandSoft: 'rgba(243,223,162,0.12)',
  sandBorder: 'rgba(243,223,162,0.35)',

  // Semantic aliases. Destructive actions share Brick's hue so the UI has one
  // red; success reuses Tide because "synced" is the brand's success state.
  success: '#9DD0D4',
  warning: '#F3DFA2',
  danger: '#E48A75',
  dangerSoft: 'rgba(187,68,48,0.14)',
  dangerBorder: 'rgba(187,68,48,0.4)',
};

export const font = {
  family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  mono: "'SF Mono', Menlo, Consolas, monospace",
};

export const type = {
  xs: '11px',
  sm: '12px',
  md: '13px',
  base: '14px',
  lg: '15px',
  xl: '18px',
  title: '20px',
  display: '26px',
};

export const radius = {
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  pill: '999px',
};

export const shadow = {
  card: '0 1px 2px rgba(0,0,0,0.25)',
  menu: '0 8px 24px rgba(0,0,0,0.45), 0 0 0 1px rgba(239,230,221,0.06)',
  dialog: '0 24px 64px rgba(0,0,0,0.55), 0 0 0 1px rgba(239,230,221,0.06)',
};

export const transition = 'background-color 120ms ease, border-color 120ms ease, color 120ms ease, opacity 120ms ease';

// Small uppercase section label, as in the storage card template.
export const label = {
  fontSize: type.xs,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: colors.textMuted,
};

export const heading = {
  page: { fontSize: type.lg, fontWeight: 600, color: colors.text, letterSpacing: '-0.01em', margin: 0 },
  section: { fontSize: type.base, fontWeight: 600, color: colors.text, letterSpacing: '-0.01em', margin: 0 },
  display: { fontSize: type.title, fontWeight: 600, color: colors.text, letterSpacing: '-0.02em', margin: 0 },
};

// Buttons. Compose: { ...btn.base, ...btn.primary } or { ...btn.base, ...btn.ghost, ...btn.sm }.
// Filled variants take the `mc-btn` class for hover; outlined/transparent
// ones take `mc-ghost` (see index.css).
const btnBase = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  height: '30px',
  padding: '0 12px',
  border: '1px solid transparent',
  borderRadius: radius.md,
  fontFamily: 'inherit',
  fontSize: type.md,
  fontWeight: 500,
  letterSpacing: '-0.01em',
  lineHeight: 1,
  color: colors.text,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  transition,
};

export const btn = {
  base: btnBase,
  primary: { background: colors.accent, color: colors.onAccent, fontWeight: 600 },
  sync: { background: colors.sync, color: colors.onSync, fontWeight: 600 },
  ghost: { borderColor: colors.borderStrong, color: colors.textSecondary },
  subtle: { color: colors.textSecondary },
  danger: { borderColor: colors.dangerBorder, color: colors.danger },
  sm: { height: '26px', padding: '0 10px', fontSize: type.sm },
  lg: { height: '36px', padding: '0 16px', fontSize: type.base },
  icon: { width: '30px', padding: 0 },
  iconSm: { width: '26px', height: '26px', padding: 0 },
  block: { width: '100%' },
};

export const input = {
  width: '100%',
  height: '32px',
  padding: '0 10px',
  border: `1px solid ${colors.borderStrong}`,
  borderRadius: radius.md,
  background: colors.inputBg,
  color: colors.text,
  fontFamily: 'inherit',
  fontSize: type.md,
  outline: 'none',
  boxSizing: 'border-box',
  transition,
};

export const select = {
  ...input,
  paddingRight: '28px',
  cursor: 'pointer',
  appearance: 'none',
  WebkitAppearance: 'none',
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 16 16' fill='none' stroke='%23EFE6DD' stroke-opacity='0.5' stroke-width='1.6'%3E%3Cpath d='M4 6l4 4 4-4'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 8px center',
};

export const card = {
  background: colors.raised,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.lg,
};

export const dialog = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: colors.scrim,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
    padding: '20px',
  },
  card: {
    width: '100%',
    maxWidth: '440px',
    background: colors.raised,
    border: `1px solid ${colors.borderStrong}`,
    borderRadius: radius.xl,
    padding: '20px',
    boxShadow: shadow.dialog,
  },
  title: { ...heading.page, marginBottom: '2px' },
  subtitle: { fontSize: type.md, color: colors.textMuted, marginBottom: '16px', wordBreak: 'break-all' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '18px' },
};

// Status colors for the NAS "live" dot. The brand's red dot means synced and
// live, so a connected drive shows Brick, exactly like the logo.
export const status = {
  live: colors.sync,
  checking: colors.sand,
  offline: colors.textFaint,
};
